/**
 * Android hardware/system back-button handler.
 *
 * Without an explicit listener, Capacitor delegates the Android back press
 * to `App.exitApp()` once the WebView has no more history entries — meaning
 * a tap on the IMEI step exits the entire app, which surprises users coming
 * from Instagram / Facebook / WhatsApp where back walks one screen at a
 * time and a double-press is required to actually exit.
 *
 * This module installs the standard Android pattern:
 *
 *   1. If a sheet/overlay/modal is open → close it.
 *   2. Else if the page has its own "go back" affordance → call it.
 *   3. Else if the WebView has history → window.history.back().
 *   4. Else show a "Press back again to exit" toast. A second press within
 *      `EXIT_WINDOW_MS` actually exits; anything else cancels the prompt.
 *
 * Each consumer registers a `BackHandler` (priority + handle()-returns
 * true-when-consumed). The composable runs them in priority order on every
 * back press and stops on the first one that returns true.
 */
import { ref } from 'vue';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';

export type BackHandler = {
  /** Higher = runs first. Sheets/modals should use 100+, page navigation 50,
   *  default exit-confirmation 0. */
  priority: number;
  /** Returns true when the handler consumed the back press. */
  handle: () => boolean | Promise<boolean>;
};

const handlers = ref<BackHandler[]>([]);
const exitToast = ref<string>('');
const EXIT_WINDOW_MS = 2000;
let lastExitPress = 0;
let listener: PluginListenerHandle | null = null;
let booted = false;

export function useAndroidBackButton() {
  return { exitToast, registerBackHandler, unregisterBackHandler };
}

export function registerBackHandler(h: BackHandler): void {
  handlers.value = [...handlers.value, h].sort((a, b) => b.priority - a.priority);
}

export function unregisterBackHandler(h: BackHandler): void {
  handlers.value = handlers.value.filter((x) => x !== h);
}

/** Call once at app boot. Wires the Capacitor App backButton listener. */
export async function bootAndroidBackButton(): Promise<void> {
  if (booted) return;
  booted = true;

  if (!Capacitor.isNativePlatform()) return;

  try {
    listener = await CapApp.addListener('backButton', async (_ev) => {
      for (const h of handlers.value) {
        const consumed = await h.handle();
        if (consumed) return;
      }

      // Fallback: WebView history walk, then double-press-to-exit.
      if (window.history.length > 1) {
        window.history.back();
        return;
      }

      const now = Date.now();
      if (now - lastExitPress < EXIT_WINDOW_MS) {
        try {
          await CapApp.exitApp();
        } catch {
          /* exitApp can throw if the platform doesn't allow it (e.g. iOS) */
        }
        return;
      }
      lastExitPress = now;
      exitToast.value = 'Press back again to exit';
      window.setTimeout(() => {
        // Auto-clear the toast after the window closes — also resets
        // the lastExitPress timer so the next back press starts fresh.
        if (Date.now() - lastExitPress >= EXIT_WINDOW_MS) exitToast.value = '';
      }, EXIT_WINDOW_MS + 100);
    });
  } catch {
    /* App plugin missing — fall back to default behaviour */
  }
}

/** Optional teardown — only useful in tests/HMR. */
export async function shutdownAndroidBackButton(): Promise<void> {
  try { await listener?.remove(); } catch { /* ignore */ }
  listener = null;
  booted = false;
}
